import { useEffect, useState } from "react";

const KEY = "nexto_engine_sound";
const stored = typeof window !== "undefined" ? window.localStorage?.getItem(KEY) : null;
let enabled = stored == null ? true : stored === "1";
const subs = new Set();
let ctx;

const getCtx = () => {
  const AC = window.AudioContext || window.webkitAudioContext;
  if (!AC) return null;
  ctx = ctx || new AC();
  if (ctx.state === "suspended") ctx.resume();
  return ctx;
};

export const isSoundOn = () => enabled;
export const setSoundOn = (on) => {
  enabled = on;
  try { localStorage.setItem(KEY, on ? "1" : "0"); } catch (e) { /* private mode */ }
  if (on) getCtx();
  subs.forEach((f) => f(on));
};

export const useEngineSound = () => {
  const [on, setOn] = useState(enabled);
  useEffect(() => { subs.add(setOn); return () => subs.delete(setOn); }, []);
  return [on, () => setSoundOn(!enabled)];
};

const tone = (c, { type = "sine", f0, f1 = f0, at = 0, dur, gain }) => {
  const t = c.currentTime + at;
  const o = c.createOscillator();
  const g = c.createGain();
  o.type = type;
  o.frequency.setValueAtTime(f0, t);
  if (f1 !== f0) o.frequency.exponentialRampToValueAtTime(f1, t + dur);
  g.gain.setValueAtTime(0.0001, t);
  g.gain.exponentialRampToValueAtTime(gain, t + Math.min(0.03, dur * 0.2));
  g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
  o.connect(g).connect(c.destination);
  o.start(t); o.stop(t + dur + 0.02);
};

const play = (fn) => {
  if (!enabled) return;
  try { const c = getCtx(); if (c) fn(c); } catch (e) { /* audio blocked — non-critical */ }
};

// Low-frequency diagnostic pulse (fires with each scan ripple while frozen).
export const playScanPulse = () => play((c) => {
  tone(c, { f0: 52, f1: 38, dur: 0.55, gain: 0.16 });
  tone(c, { f0: 104, f1: 90, dur: 0.35, gain: 0.03 });
});

// Sharp mechanical "Data Lock" click when the frame freezes.
export const playLock = () => play((c) => {
  tone(c, { type: "square", f0: 1900, f1: 900, dur: 0.045, gain: 0.06 });
  tone(c, { f0: 3400, f1: 2200, at: 0.02, dur: 0.12, gain: 0.045 });
  tone(c, { f0: 140, f1: 70, dur: 0.16, gain: 0.07 });
});

// High-tech "UI Shimmer" as the HUD leads and skeleton draw out.
export const playShimmer = () => play((c) => {
  [1320, 1760, 2340, 2960].forEach((f, i) => tone(c, { f0: f, f1: f * 1.12, at: 0.05 * i, dur: 0.22, gain: 0.028 }));
  tone(c, { f0: 440, f1: 1320, dur: 0.42, gain: 0.035 });
  tone(c, { type: "triangle", f0: 880, f1: 2640, at: 0.06, dur: 0.4, gain: 0.015 });
});
export const playBloom = playShimmer;

// Bright two-note "Success Chime" when a Technical Pulse hexagon lights up.
export const playPulseChime = () => play((c) => {
  tone(c, { f0: 1046, f1: 1046, dur: 0.18, gain: 0.05 });
  tone(c, { type: "triangle", f0: 1568, f1: 1568, at: 0.11, dur: 0.32, gain: 0.045 });
  tone(c, { f0: 2093, f1: 2350, at: 0.2, dur: 0.4, gain: 0.02 });
  tone(c, { f0: 262, f1: 196, dur: 0.3, gain: 0.03 });
});

// Low "System Startup" hum when a report opens — a slow swell, kept well under speech level.
export const playStartup = () => play((c) => {
  const t = c.currentTime;
  const g = c.createGain();
  g.gain.setValueAtTime(0.0001, t);
  g.gain.exponentialRampToValueAtTime(0.09, t + 0.9);
  g.gain.exponentialRampToValueAtTime(0.0001, t + 2.4);
  g.connect(c.destination);
  [48, 48.7, 96].forEach((f) => { const o = c.createOscillator(); o.type = "sine"; o.frequency.value = f; o.connect(g); o.start(t); o.stop(t + 2.5); });
  tone(c, { f0: 1760, f1: 2640, at: 1.0, dur: 0.35, gain: 0.02 });
});
